import { _ } from "../../polyglot/polyglot.js";
import {
	cancelLinkRenderer,
	footerButtonsRenderer,
	makeCancelAndDeleteFooterBuilder,
	makeCancelAndOkFooterBuilder,
	makeOkFooterBuilder,
} from "./footers/footers.js";
import { exclamationTriangleIcon } from "../../elements/icons/index.js";
import {
	horizontal,
	vertical,
} from "@foretagsplatsen/finsit-design-system/constants/direction";
import { large } from "../../elements/size/index.js";
import {
	leftAlign,
	topAlign,
} from "@foretagsplatsen/finsit-design-system/constants/alignment";
import FormBuilder from "../forms/FormBuilder.js";
import NewDialog from "../NewDialog/NewDialog.js";
import spacer from "@foretagsplatsen/finsit-design-system/widget-js/elements/spacer";
import stacker from "@foretagsplatsen/finsit-design-system/widget-js/elements/stacker";
import text from "@foretagsplatsen/finsit-design-system/widget-js/elements/text";
import WaitingButtonWidget from "../WaitingButtonWidget/WaitingButtonWidget.js";

/**
 * This file exports a few helper functions to generate instance
 * of NewDialog.js.
 */

/**
 * Create a dialog with an informative message.
 *
 * @param {object} options - Object parameters
 * @param {string} options.title - Title of the dialog
 * @param {string} options.subtitle - Subtitle of the dialog
 * @param {string} options.body - Body of the dialog
 * @param {string} options.okLabel - Label of the ok button
 * @param {function} options.callback - Function to call when the ok button is
 *   clicked
 * @param {string} options.contentCssClass - CSS class to apply to the dialog
 * @param {string} options.dataTestName - Data test name to apply to the dialog
 *   for testing purpose
 * @return {NewDialog}
 */
export function informDialog({
	title,
	subtitle,
	body,
	okLabel,
	callback,
	contentCssClass,
	dataTestName,
}) {
	return new NewDialog({
		title,
		subtitle,
		body,
		footerBuilder: makeOkFooterBuilder({ okLabel, callback }),
		contentCssClass,
		dataTestName,
	});
}

/**
 * Return a dialog asking confirmation. In case the confirmation is about a
 * very important decision, prefer using `confirmDeletionDialog` instead.
 */
export function confirmDialog({
	title,
	subtitle,
	body,
	okLabel,
	cancelLabel,
	callback,
	extraButtonsRenderer,
	contentCssClass,
	dataTestName,
}) {
	return new NewDialog({
		title,
		subtitle,
		body,
		contentCssClass,
		dataTestName,
		footerBuilder: makeCancelAndOkFooterBuilder({
			okLabel,
			cancelLabel,
			extraButtonsRenderer,
			callback: ({ hide }) => {
				hide();
				callback();
			},
		}),
	});
}

/**
 * Return a dialog containing a form. This is useful when asking a user to
 * configure something.
 */
export function configureDialog({
	title,
	subtitle,
	okLabel = _("Ok"),
	waitingOkLabel,
	contentCssClass,
	dataTestName,
	cancelLabel = _("Cancel"),
	formSpec,
	formBuilder,
	extraButtonsRenderer,
}) {
	const form = new FormBuilder({
		...formSpec,
		showActions: false,
		saveAction: null,
	});

	formBuilder(form);

	const footerBuilder = ({ hide }) => {
		let promise = Promise.resolve();

		form.onSubmit.register(() => {
			promise = Promise.resolve(
				formSpec?.saveAction?.(form.getObjectToSave()),
			).then(hide);

			return promise;
		});

		const okButtonRenderer = () =>
			new WaitingButtonWidget({
				isPrimary: true,
				label: okLabel,
				waitingLabel: waitingOkLabel,
				onClick: () => {
					form.submit();

					return promise;
				},
				dataTestName: "ok",
			});

		return footerButtonsRenderer({
			mainButtons: [
				cancelLinkRenderer({ hide, cancelLabel }),
				okButtonRenderer(),
			],
			extraButtons: extraButtonsRenderer?.({ hide }),
		});
	};

	return new NewDialog({
		title,
		subtitle,
		body: form,
		contentCssClass,
		dataTestName,
		footerBuilder,
	});
}

/**
 * Return a dialog asking confirmation about a modification that has wide
 * impact. The ok button is made red and is only activated after a few seconds.
 */
export function warnDialog({
	name,
	title = _("Modifying {name}", {
		name,
	}),
	body = _("Are you sure you want to modify {name}?", { name }),
	okLabel = _("Modify"),
	cancelLabel,
	millisecondsBeforeActivation,
	callback,
	onCancel,
	cssClass = "dark-dialog",
}) {
	return new NewDialog({
		title,
		contentCssClass: cssClass,
		body: (html) => {
			html.div(
				{ class: "warning-dialog-body" },
				stacker(
					{
						direction: horizontal,
						align: topAlign,
						isBlock: true,
					},

					spacer(
						{
							bottom: true,
							align: { vertical: topAlign },
						},
						exclamationTriangleIcon({ size: large }),
					),

					stacker(
						{
							direction: vertical,
							isBlock: true,
							align: leftAlign,
						},
						text({ emphasize: true }, _("Warning!")),
						text(body),
					),
				),
			);
		},
		footerBuilder: makeCancelAndDeleteFooterBuilder({
			okLabel,
			cancelLabel,
			millisecondsBeforeActivation,
			callback,
			onCancel,
		}),
	});
}

/**
 * Return a dialog asking confirmation about an important decision. The ok
 * button is made red and is only activated after a few seconds.
 */
export function confirmDeletionDialog({
	name,
	title = _("Delete {name}?", { name }),
	subtitle,
	contentCssClass,
	body = _("Are you sure you want to permanently delete {name}?", { name }),
	okLabel,
	cancelLabel,
	millisecondsBeforeActivation,
	callback,
}) {
	return new NewDialog({
		title,
		subtitle,
		body,
		contentCssClass,
		footerBuilder: makeCancelAndDeleteFooterBuilder({
			okLabel,
			cancelLabel,
			millisecondsBeforeActivation,
			callback,
		}),
	});
}
